/**
 * Shared domain vocabulary for the counterpoint engine.
 *
 * Every module under `src/core` is pure: no DOM, no React, no audio. That makes
 * the music theory unit-testable on its own and keeps the UI free to render it
 * however it likes (SVG today, a native canvas tomorrow).
 */

/** The seven letter names, indexed C=0 … B=6. */
export type Letter = 'C' | 'D' | 'E' | 'F' | 'G' | 'A' | 'B';

/** Equal division of the octave used by an exercise. */
export type Edo = 12 | 31;

/** Major or minor. Modes beyond these are a future concern. */
export type Mode = 'major' | 'minor';

/**
 * A key signature the trainer can generate exercises in.
 *
 * `sig` is the signed accidental count: positive for sharps, negative for flats.
 */
export interface MusicKey {
  readonly id: string;
  readonly label: string;
  readonly tonic: Letter;
  readonly mode: Mode;
  readonly sig: number;
}

/** Which voice carries the given melody. The other voice is the student's. */
export type CfPosition = 'lower' | 'upper';

/**
 * Which teacher's conventions the grader enforces.
 *
 * `lim` follows Skylar Lim's three-minute rule set; `gran` follows Jacob Gran's
 * slightly different dialect (see DESIGN.md for the divergence table).
 */
export type RulesProfile = 'lim' | 'gran';

/**
 * One exercise: a fixed cantus firmus and the counterpoint line being written
 * against it.
 *
 * In 12-EDO, pitches are diatonic indices and accidentals come from the key. In
 * 31-EDO, pitches are absolute EDO-step indices whose pitch classes encode the
 * canonical circle-of-fifths spelling. Pitch helpers expose staff position and
 * tuning-step distance without leaking that representation into the rules.
 *
 * A `null` in `cp` is a rest — the state every measure starts in.
 */
export interface Exercise {
  /** Missing on legacy saves, where it means the default 12-EDO. */
  readonly edo?: Edo;
  readonly key: MusicKey;
  readonly cfPos: CfPosition;
  readonly cf: readonly number[];
  readonly cp: readonly (number | null)[];
}

/** Measure count of an exercise. The two voices are always the same length. */
export const measureCount = (ex: Exercise): number => ex.cf.length;

/** A vertical interval between the two voices in one measure. */
export interface IntervalInfo {
  /** Diatonic distance, upper minus lower; 0 is a unison, 7 an octave. */
  readonly dd: number;
  /** The same distance in the exercise's EDO steps, so quality can be judged. */
  readonly steps: number;
  /** Interval number reduced to its simple class: a 10th reads as 3, a 12th as 5. */
  readonly num: number;
  /** `dd % 7` — the class used by the parallel/run rules. */
  readonly cls: number;
  readonly consonant: boolean;
  readonly perfect: boolean;
}

/** Motion between two consecutive measures. */
export type Motion =
  | 'c' // contrary
  | 's' // similar
  | 'o' // oblique
  | 'p'; // parallel perfect (a similar motion the rules single out)

/** Which graded dimension a finding belongs to. */
export type Dimension = 'cons' | 'form' | 'perf' | 'run' | 'rep' | 'mel';

/** Keys into the tip text bank; each finding names the lesson behind it. */
export type TipCode =
  | 'P4' | 'TRI' | 'DISS' | 'UNI' | 'PAR' | 'DIR' | 'RUN' | 'REP'
  | 'START' | 'PP2' | 'WIDE' | 'PEAK' | 'CAD' | 'FINAL' | 'XV'
  | 'AUG2' | 'LEAP' | 'IDLE';

/** One rule violation, always anchored to a measure so the UI can point at it. */
export interface Finding {
  /** Zero-based measure the finding is drawn on. */
  readonly measure: number;
  readonly code: TipCode;
  readonly severity: 'error' | 'warning';
  /**
   * Whether the legality filter treats this as disqualifying. Errors always are;
   * some warnings (unsingable leaps, tritones) are too, because offering them as
   * "allowed notes" would teach the wrong thing.
   */
  readonly hard: boolean;
  readonly dimension: Dimension;
  readonly message: string;
}

/** Everything the deterministic checker knows about a position. */
export interface Analysis {
  /** One entry per measure; `null` where the counterpoint still rests. */
  readonly intervals: readonly (IntervalInfo | null)[];
  /** Length `measures - 1`; index `k` is the motion from measure `k` to `k + 1`. */
  readonly motions: readonly (Motion | null)[];
  readonly findings: readonly Finding[];
  /** True when the counterpoint's high point occurs exactly once. */
  readonly climaxOk: boolean;
}

/** A legal candidate pitch for one measure of the counterpoint. */
export interface Candidate {
  /** Diatonic index. */
  readonly d: number;
  /** True in the measures governed by an opening or cadence formula. */
  readonly formula: boolean;
}

/** One row of the grade report. */
export interface GradeDimension {
  readonly name: string;
  readonly max: number;
  readonly got: number;
  /** Assists that, if engaged, mark this dimension "assisted" in the report. */
  readonly assists: readonly AssistId[];
}

/** The two live helpers. See DESIGN.md, "The clutch system". */
export type AssistId = 'labels' | 'allowed';

/** The deterministic grade. `total` is withheld in the UI until `complete`. */
export interface GradeReport {
  readonly dims: readonly GradeDimension[];
  readonly total: number;
  readonly max: number;
  readonly complete: boolean;
  readonly analysis: Analysis;
}
